import { Tooltip, Typography } from "antd";
import { CHART_INK, CHART_MARKS } from "./chartTheme";

export interface ShareSegment {
  key: string;
  label: string;
  value: number;
  color: string;
  /** Shown in the hover readout in place of the raw value (money, quantities). */
  display?: string;
}

/**
 * A part-to-whole bar: one horizontal track split into its segments, with the legend
 * carrying identity so nothing depends on matching a colour by eye.
 *
 * Built from plain elements rather than a chart library because the two things that make
 * a stacked bar readable are pure layout — a 2px gap in the surface colour between
 * touching segments, and a label that is either placed where it genuinely fits or moved
 * to the legend and the hover readout. Neither is worth an SVG renderer.
 */
export function ShareBar({ segments, height = 20 }: { segments: ShareSegment[]; height?: number }) {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const shown = segments.filter((s) => s.value > 0);
  if (total <= 0 || shown.length === 0) return null;

  // One segment is the whole: a full-width bar reading 100% is a one-bar chart, so the
  // figure stands on its own instead.
  if (shown.length === 1) {
    return (
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <Typography.Text strong style={{ fontSize: 20 }}>
          {shown[0].display ?? shown[0].value}
        </Typography.Text>
        <Typography.Text style={{ color: CHART_INK.secondary, fontSize: 12 }}>{shown[0].label}</Typography.Text>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", gap: CHART_MARKS.gap, height, borderRadius: 4, overflow: "hidden" }}>
        {shown.map((segment) => (
          <Tooltip
            key={segment.key}
            title={`${segment.display ?? segment.value} · ${segment.label} (${Math.round((segment.value / total) * 100)}%)`}
          >
            <div
              style={{
                // Flex-basis by share, so the gaps come out of the track rather than
                // distorting the proportions the reader is comparing.
                flex: `${segment.value} 0 0`,
                background: segment.color,
                minWidth: 3,
              }}
            />
          </Tooltip>
        ))}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", marginTop: 8 }}>
        {shown.map((segment) => (
          <span key={segment.key} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: segment.color, flex: "none" }} />
            <span style={{ color: CHART_INK.secondary }}>{segment.label}</span>
            <span style={{ color: CHART_INK.primary, fontWeight: 600 }}>{segment.display ?? segment.value}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
